/**
 * Which demo rule a stream screen counts by.
 *
 * By default — the organization's demo policy, as every dashboard: demo calls
 * are counted only while the organization has no real ones.
 *
 * Inside the Voice of Customer section an exact window (`from` and `to` in the
 * address) is the monthly Voice of Customer email's: its button is the only
 * way in with one. The email never counts demo calls, so every block of the
 * section — the Objections screen on Sales included — counts that window
 * without them, and the screen the button opens shows the email's numbers.
 * A link that names the rule itself (`demo`) wins over both. The Objections route keeps the organization's policy on its own exact
 * window (the daily digest's link): that digest counts by the policy.
 *
 * `overridesPolicy` — the screen counts by another rule than the organization's.
 * The «Records» list a number opens always counts by the policy, so such a
 * number must stay a number: a link would open a list of other records.
 */
import { useAuth } from '@/contexts/UnifiedAuthContext';
import { useDemoFilterFromAuth } from '@/features/demo/useDemoFilter';
import type { ObjectionsSearch } from '../objections/objections-search';

export function useStreamDemoFilter(
  search: Pick<ObjectionsSearch, 'from' | 'to' | 'demo'>,
  scope: 'voice-section' | 'objections'
): { includeDemo: 'yes' | 'no'; ready: boolean; overridesPolicy: boolean } {
  const { apiClient, orgId } = useAuth();
  const policy = useDemoFilterFromAuth(apiClient, orgId);
  // A rule the link names (the unified dashboard's «Отчёт →» carries its own
  // filter, which may differ from the policy) is the one the card counted by.
  // While the policy is unknown a named rule is taken to differ from it: a
  // link that appears and then goes is worse than one that waits.
  if (search.demo) {
    return {
      includeDemo: search.demo,
      ready: true,
      overridesPolicy: policy.isLoading || search.demo !== policy.includeDemo,
    };
  }
  if (scope === 'voice-section' && search.from && search.to) {
    return {
      includeDemo: 'no',
      ready: true,
      overridesPolicy: policy.isLoading || policy.includeDemo !== 'no',
    };
  }
  // Not before the policy is known: a scan sent with the default would be
  // thrown away, and a demo-only org would first see an empty screen
  // (code-review #1208).
  return {
    includeDemo: policy.includeDemo,
    ready: !policy.isLoading,
    overridesPolicy: false,
  };
}
